// Draws the share card for a finished workout: a picture with the headline
// numbers and a small graph, to paste into WhatsApp or a story.
//
// Takes the plain data from utils/workoutShare.js shareCardData() and has no
// imports, so the same file renders in the app and in the design preview.

export const CARD_WIDTH = 1080;
export const CARD_HEIGHT = 800;

export const CARD_THEMES = ["dark", "light"];
export const DEFAULT_CARD_OPTIONS = { theme: "dark" };

// How many numbers the card holds: up to three across, in one or two rows.
export const MAX_CARD_STATS = 6;
const ROW_H = 108;

// The site's neon red (globals.css --work-color), a shade deeper on white.
const THEMES = {
  dark: {
    bgFrom: "#0b0d17",
    bgTo: "#1c0f1d",
    ink: "#ffffff",
    muted: "rgba(255,255,255,0.55)",
    accent: "#ff1f4b",
    quiet: "rgba(255,255,255,0.22)",
    rest: "#80889b",
    glow: true,
  },
  light: {
    bgFrom: "#ffffff",
    bgTo: "#f3f1f4",
    ink: "#0f172a",
    muted: "#64748b",
    accent: "#e11d48",
    quiet: "rgba(15,23,42,0.16)",
    rest: "#5b6577",
    glow: false,
  },
};

const FONT = '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const font = (weight, px) => `${weight} ${px}px ${FONT}`;

const hexA = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

// Shrinks a line until it fits, then cuts it with an ellipsis if even the
// smallest size is too wide.
const fitText = (ctx, text, x, y, maxW, weight, px, minPx) => {
  let size = px;
  ctx.font = font(weight, size);
  while (size > minPx && ctx.measureText(text).width > maxW) {
    size -= 2;
    ctx.font = font(weight, size);
  }
  let out = text;
  while (out.length > 1 && ctx.measureText(out).width > maxW) {
    out = `${out.slice(0, -2).trimEnd()}…`;
  }
  ctx.fillText(out, x, y);
  return size;
};

/** The numbers the card shows, as many as fit. */
export const cardStats = (data) => (data?.stats || []).slice(0, MAX_CARD_STATS);

/**
 * Where each number sits: [{ stat, col, row }] and the column count. Up to
 * three go in one row. More fill two rows, and an average and its max (same
 * `group`) share a column, one above the other, so they read together; the
 * rest pair up in order.
 */
export const cardLayout = (stats) => {
  if (stats.length <= 3)
    return { cols: Math.max(1, stats.length), cells: stats.map((stat, i) => ({ stat, col: i, row: 0 })) };
  const columns = [];
  const singles = [];
  for (let i = 0; i < stats.length; i++) {
    const next = stats[i + 1];
    if (next && stats[i].group && next.group === stats[i].group) {
      columns.push({ at: i, items: [stats[i], next] });
      i += 1;
    } else singles.push({ at: i, stat: stats[i] });
  }
  for (let i = 0; i < singles.length; i += 2) {
    columns.push({ at: singles[i].at, items: singles.slice(i, i + 2).map((x) => x.stat) });
  }
  columns.sort((a, b) => a.at - b.at);
  return {
    cols: columns.length,
    cells: columns.flatMap((c, col) => c.items.map((stat, row) => ({ stat, col, row }))),
  };
};

// The graph's points in card space, with gaps kept as breaks.
const graphPoints = (graph, x, y, w, h) => {
  const n = graph.values.length;
  const span = graph.max - graph.min;
  // A flat line sits in the middle rather than on the floor.
  const pad = span > 0 ? span * 0.12 : 1;
  const lo = graph.min - pad;
  const hi = graph.max + pad;
  return graph.values.map((v, i) =>
    v == null
      ? null
      : {
          x: x + (n === 1 ? w / 2 : (i / (n - 1)) * w),
          y: y + h - ((v - lo) / (hi - lo || 1)) * h,
        },
  );
};

// Runs of consecutive points, so a gap in the strap breaks the line.
const runs = (pts) => {
  const out = [];
  let cur = [];
  for (const p of pts) {
    if (p) cur.push(p);
    else if (cur.length) {
      out.push(cur);
      cur = [];
    }
  }
  if (cur.length) out.push(cur);
  return out;
};

const traceLine = (ctx, run) => {
  ctx.beginPath();
  run.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
};

// One unbroken run of points: a fading area with a solid, glowing line on top.
const drawRun = (ctx, run, y, h, t) => {
  const grad = ctx.createLinearGradient(0, y, 0, y + h);
  grad.addColorStop(0, hexA(t.accent, t.glow ? 0.42 : 0.24));
  grad.addColorStop(1, hexA(t.accent, 0));
  traceLine(ctx, run);
  ctx.lineTo(run[run.length - 1].x, y + h);
  ctx.lineTo(run[0].x, y + h);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.save();
  if (t.glow) {
    ctx.shadowColor = hexA(t.accent, 0.75);
    ctx.shadowBlur = 24;
  }
  traceLine(ctx, run);
  ctx.strokeStyle = t.accent;
  ctx.lineWidth = 6;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.stroke();
  ctx.restore();
};

// A measured series, broken where the sensor dropped out.
const drawLine = (ctx, graph, x, y, w, h, t) => {
  for (const run of runs(graphPoints(graph, x, y, w, h))) drawRun(ctx, run, y, h, t);
};

/**
 * No strap: the steps drawn like the heart-rate line, each step's effort
 * level as a plateau (work high, rest low) joined by short eased slopes, so
 * the intervals read like a measured session. `curve` "steps" makes the
 * slopes straight and steep instead.
 */
export const effortPoints = (phases, x, y, w, h, curve = "smooth") => {
  const pts = [];
  const level = (p) => y + h - Math.max(0.04, p.level ?? 1) * h * 0.92;
  phases.forEach((p, i) => {
    const x0 = x + p.from * w;
    const x1 = x + p.to * w;
    const ly = level(p);
    const next = phases[i + 1];
    if (i === 0) pts.push({ x: x0, y: ly });
    if (!next) {
      pts.push({ x: x1, y: ly });
      return;
    }
    // The slope into the next step straddles the boundary.
    const ny = level(next);
    const half = Math.min(curve === "steps" ? 4 : 14, (x1 - x0) / 3, (x + next.to * w - x1) / 3);
    pts.push({ x: x1 - half, y: ly });
    const n = curve === "steps" ? 1 : 8;
    for (let k = 1; k <= n; k++) {
      const f = k / n;
      const e = curve === "steps" ? f : (1 - Math.cos(Math.PI * f)) / 2;
      pts.push({ x: x1 - half + 2 * half * f, y: ly + (ny - ly) * e });
    }
  });
  return pts;
};

const isHard = (p) => p.kind === "work" || p.kind === "other";

// A step's box: its span and top, with `gap` px between neighbours.
const stepBox = (p, x, y, w, h, gap) => {
  const x0 = x + p.from * w + gap / 2;
  const x1 = x + p.to * w - gap / 2;
  return { x0, x1, top: y + h - Math.max(0.04, p.level ?? 1) * h * 0.92 };
};

const fade = (ctx, colour, top, bottom, strength) => {
  const g = ctx.createLinearGradient(0, top, 0, bottom);
  g.addColorStop(0, hexA(colour, strength));
  g.addColorStop(1, hexA(colour, 0));
  return g;
};

const glowOn = (ctx, t, colour, on) => {
  if (t.glow && on) {
    ctx.shadowColor = hexA(colour, 0.75);
    ctx.shadowBlur = 22;
  }
};

const roundTop = (ctx, x0, x1, top, bottom, r) => {
  const rr = Math.max(0, Math.min(r, (x1 - x0) / 2, (bottom - top) / 2));
  ctx.beginPath();
  ctx.moveTo(x0, bottom);
  ctx.lineTo(x0, top + rr);
  ctx.arcTo(x0, top, x0 + rr, top, rr);
  ctx.lineTo(x1 - rr, top);
  ctx.arcTo(x1, top, x1, top + rr, rr);
  ctx.lineTo(x1, bottom);
};

/**
 * No strap: the steps in two colours, work in the accent and the rest grey.
 * `style` picks how they're drawn (the design preview shows each):
 *   "outlined" separate rounded blocks, a faded fill and a bright outline
 *   "joined"   blocks side by side, a faded fill and one outline along the top
 *   "hollow"   outline only
 *   "line"     the stepped line, its colour switching between work and rest
 */
const drawEffort = (ctx, phases, x, y, w, h, t, style = "outlined") => {
  if (!phases?.length) return;
  const bottom = y + h;
  const colourOf = (p) => (isHard(p) ? t.accent : t.rest);

  if (style === "line") {
    // Split the eased line at each boundary and draw each step in its colour.
    // A slope between work and rest belongs to the work, so each interval
    // reads as one red shape; rests are drawn first, under it.
    const pts = effortPoints(phases, x, y, w, h, "smooth");
    const order = [...phases].sort((a, b) => Number(isHard(a)) - Number(isHard(b)));
    // Half-width of the slope after step i, as effortPoints makes it.
    const half = (i) =>
      i < 0 || i >= phases.length - 1
        ? 0
        : Math.min(14, ((phases[i].to - phases[i].from) * w) / 3, ((phases[i + 1].to - phases[i + 1].from) * w) / 3);
    order.forEach((p) => {
      const i = phases.indexOf(p);
      const sign = isHard(p) ? 1 : -1;
      const lo = x + p.from * w - sign * half(i - 1);
      const hi = x + p.to * w + sign * half(i);
      const run = pts.filter((q) => q.x >= lo - 0.01 && q.x <= hi + 0.01);
      if (run.length < 2) return;
      const c = colourOf(p);
      traceLine(ctx, run);
      ctx.lineTo(run[run.length - 1].x, bottom);
      ctx.lineTo(run[0].x, bottom);
      ctx.closePath();
      ctx.fillStyle = fade(ctx, c, y, bottom, isHard(p) ? 0.42 : 0.2);
      ctx.fill();
      ctx.save();
      glowOn(ctx, t, c, isHard(p));
      traceLine(ctx, run);
      ctx.strokeStyle = c;
      ctx.lineWidth = 6;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.stroke();
      ctx.restore();
    });
    return;
  }

  const gap = style === "joined" ? 0 : 6;
  // Rests first, so a work block's glow sits over its neighbours.
  const order = [...phases].sort((a, b) => Number(isHard(a)) - Number(isHard(b)));
  for (const p of order) {
    const { x0, x1, top } = stepBox(p, x, y, w, h, gap);
    if (x1 - x0 < 1) continue;
    const c = colourOf(p);
    const hard = isHard(p);
    if (style !== "hollow") {
      roundTop(ctx, x0, x1, top, bottom, style === "joined" ? 4 : 10);
      ctx.closePath();
      ctx.fillStyle = fade(ctx, c, top, bottom, hard ? 0.5 : 0.25);
      ctx.fill();
    }
    ctx.save();
    glowOn(ctx, t, c, hard);
    ctx.strokeStyle = c;
    ctx.lineWidth = style === "hollow" ? 4 : 5;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    if (style === "joined") {
      ctx.beginPath();
      ctx.moveTo(x0, top);
      ctx.lineTo(x1, top);
    } else {
      roundTop(ctx, x0, x1, top, bottom - 2, 10);
      if (style === "hollow") ctx.closePath();
    }
    ctx.stroke();
    ctx.restore();
  }
  if (style === "joined") {
    // Upright edges where the level changes, in the taller step's colour.
    phases.slice(1).forEach((p, i) => {
      const prev = phases[i];
      const a = stepBox(prev, x, y, w, h, 0);
      const b = stepBox(p, x, y, w, h, 0);
      ctx.save();
      const tall = a.top < b.top ? prev : p;
      glowOn(ctx, t, colourOf(tall), isHard(tall));
      ctx.strokeStyle = colourOf(tall);
      ctx.lineWidth = 5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(b.x0, a.top);
      ctx.lineTo(b.x0, b.top);
      ctx.stroke();
      ctx.restore();
    });
  }
};

/** Whether a measured graph also knows the session's work and rest steps. */
export const hasIntervals = (graph) =>
  !!graph &&
  graph.kind !== "effort" &&
  Array.isArray(graph.phases) &&
  graph.phases.some(isHard) &&
  graph.phases.length > 1;

// The measured line over the work and rest steps: a faint red band behind
// each work step, so the heart rate's peaks line up with the intervals.
const drawBoth = (ctx, graph, x, y, w, h, t) => {
  for (const p of graph.phases) {
    if (!isHard(p)) continue;
    const x0 = x + p.from * w;
    const x1 = x + p.to * w;
    ctx.fillStyle = fade(ctx, t.accent, y, y + h, t.glow ? 0.22 : 0.16);
    ctx.fillRect(x0, y, x1 - x0, h);
  }
  drawLine(ctx, graph, x, y, w, h, t);
};

const graphCaption = (graph) =>
  graph.kind === "effort" ? "Work and rest" : `${graph.label} ${graph.min}–${graph.max} ${graph.unit}`;

/**
 * Draw the card onto a canvas at `scale` × 1080 × 800 px.
 * @param {HTMLCanvasElement} canvas
 * @param {object} data     shareCardData() output
 * @param {object} options  { theme: "dark" | "light", graph: "line" | "intervals" | "both" }
 */
export function drawWorkoutCard(canvas, data, options = {}, scale = 1) {
  const S = CARD_WIDTH;
  const H = CARD_HEIGHT;
  canvas.width = Math.round(S * scale);
  canvas.height = Math.round(H * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";

  const t = THEMES[options.theme] || THEMES.dark;
  const { facts, graph } = data;
  const M = 72;

  const bg = ctx.createLinearGradient(0, 0, S, H);
  bg.addColorStop(0, t.bgFrom);
  bg.addColorStop(1, t.bgTo);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, S, H);

  // Wordmark and date.
  ctx.fillStyle = t.accent;
  ctx.font = font(800, 28);
  ctx.letterSpacing = "6px";
  ctx.fillText("KHRONOS", M, 92);
  ctx.letterSpacing = "0px";
  ctx.textAlign = "right";
  ctx.font = font(600, 30);
  ctx.fillStyle = t.muted;
  ctx.fillText(facts.date || "", S - M, 92);
  ctx.textAlign = "left";

  ctx.fillStyle = t.ink;
  fitText(ctx, facts.title, M, 178, S - 2 * M, 800, 72, 44);

  // The numbers: one row of up to three, or two rows (2 + 2, 3 + 2, 3 + 3),
  // and the graph gives up the height of the second row.
  const { cols, cells } = cardLayout(cardStats(data));
  const rows = cells.some((c) => c.row > 0) ? 2 : 1;
  const colW = (S - 2 * M) / cols;
  cells.forEach(({ stat: s, col, row }) => {
    const cx = M + col * colW;
    const dy = row * ROW_H;
    ctx.fillStyle = t.muted;
    ctx.font = font(600, 24);
    ctx.letterSpacing = "2px";
    ctx.fillText(s.label.toUpperCase(), cx, 250 + dy);
    ctx.letterSpacing = "0px";
    ctx.fillStyle = t.ink;
    fitText(ctx, s.value, cx, 312 + dy, colW - 28, 700, 56, 34);
  });

  if (graph) {
    const extra = rows > 1 ? ROW_H : 0;
    const gy = 380 + extra;
    const gh = 330 - extra;
    const gw = S - 2 * M;
    const mode = graph.kind === "effort" ? "intervals" : hasIntervals(graph) ? options.graph || "line" : "line";
    if (mode === "intervals") {
      drawEffort(ctx, graph.phases, M, gy, gw, gh, t, options.effortStyle);
    } else if (mode === "both") {
      drawBoth(ctx, graph, M, gy, gw, gh, t);
    } else {
      drawLine(ctx, graph, M, gy, gw, gh, t);
    }
    ctx.fillStyle = t.muted;
    ctx.font = font(600, 26);
    ctx.fillText(
      mode === "line" || graph.kind === "effort"
        ? graphCaption(graph)
        : mode === "intervals"
          ? "Work and rest"
          : `${graphCaption(graph)} · work and rest`,
      M,
      H - 44,
    );
  }
  return canvas;
}

/** The card as a PNG Blob (null where the browser can't make one). */
export function workoutCardBlob(data, options = {}) {
  const canvas = document.createElement("canvas");
  drawWorkoutCard(canvas, data, options);
  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), "image/png");
    } catch {
      resolve(null);
    }
  });
}
